import { useState, useEffect, useCallback } from 'react';
import { SESSION_RESET_EVENT, getSessionEpoch } from '../../utils/sessionReset';

/**
 * Who does this BROWSER currently think is signed in?
 *
 * ─── READ THIS BEFORE USING IT FOR ANYTHING ──────────────────────────────
 * This is a CLIENT-SIDE HINT and nothing more. It decides which panel to
 * render and which React subtree to remount. It is NOT authorisation, and it
 * is not trusted by the server for anything: /api/teegee derives the real
 * owner from the session token alone, and would ignore an id sent from here.
 *
 * A student could hand-edit localStorage to make this hook report someone
 * else. All they would achieve is an empty TeeGee panel, because the server
 * would still resolve the owner from their own token — or from no token, and
 * serve guest mode.
 *
 * ─── WHY THE `identityKey` MATTERS MOST ──────────────────────────────────
 * The returned `identityKey` is used as a React `key` on the TeeGee subtree.
 * When it changes, React unmounts the old tree and mounts a fresh one — every
 * piece of useState inside is destroyed by React itself.
 *
 * That is a much stronger guarantee than "each component resets its own state
 * on an event", which is the design that produced the original bug: TeeGee's
 * messages lived in useState in a component mounted at the app root that
 * nothing ever unmounted, so no amount of storage clearing could remove them.
 * With the key in place, correctness no longer depends on every future
 * component author remembering to write a reset handler.
 */

const readIdentity = () => {
  try {
    const token = window.localStorage.getItem('session_token');
    if (!token) return { authed: false, identityKey: 'guest', token: null };

    // Only a hint for keying/telemetry. The canonical id comes from the server
    // in /context, and that is the one ever shown to the student.
    const hint =
      window.localStorage.getItem('user_id') ||
      window.localStorage.getItem('nextgen_builder_id') ||
      window.localStorage.getItem('user_pk') ||
      'unknown';

    // The token's tail is included so that signing in as the SAME account
    // again still produces a new key: a second login mints a new token, and a
    // fresh mount is the correct, cheap response to any session boundary.
    return { authed: true, identityKey: `u:${hint}:${String(token).slice(-12)}`, token };
  } catch (e) {
    // Storage blocked (private mode, or a browser configured to refuse it).
    // Guest is the safe answer: it shows a working bot and reveals nothing.
    return { authed: false, identityKey: 'guest', token: null };
  }
};

export default function useTeeGeeIdentity() {
  const [identity, setIdentity] = useState(() => ({ ...readIdentity(), epoch: getSessionEpoch() }));

  const refresh = useCallback(() => {
    setIdentity((prev) => {
      const next = { ...readIdentity(), epoch: getSessionEpoch() };
      // Avoid a re-render (and therefore a remount) when nothing changed.
      if (next.identityKey === prev.identityKey && next.epoch === prev.epoch) return prev;
      return next;
    });
  }, []);

  useEffect(() => {
    // Our own login/logout boundary, dispatched by sessionReset.
    window.addEventListener(SESSION_RESET_EVENT, refresh);

    // A logout in ANOTHER TAB. `storage` only fires in other documents, which
    // is exactly the case the in-page event cannot cover: without this, a
    // second tab keeps showing the previous user's TeeGee until it is
    // reloaded.
    const onStorage = (e) => {
      if (!e || e.key === null || ['session_token', 'user', 'user_id', 'nextgen_builder_id'].includes(e.key)) refresh();
    };
    window.addEventListener('storage', onStorage);

    // Returning to a backgrounded tab: the session may have ended elsewhere
    // while this document was not receiving events.
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener(SESSION_RESET_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  return identity;
}

export { readIdentity };
